import { I18n } from '@calendly/i18n';
import { BareButton } from '@calendly/ui/components/bare-button';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { AiNotetakerIcon, ArrowExpandIcon, SettingsIcon } from '@calendly/ui/components/icons';
import { Link } from '@calendly/ui/components/link';
import { ProTip } from '@calendly/ui/components/pro-tip';
import { RadioInput } from '@calendly/ui/components/radio-input';
import { BodyText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { colorTextStandard, textColorSecondary4 } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { AllSchedulingMethods, SchedulingMethods } from 'constants/meetings';
import { useGetUserSettingsQuery, usePatchUserSettingsMutation } from 'features/availability';
import { useFetchCalendarAccountsQuery } from 'features/calendar_connections';
const containerCss = "cmx5wbk";
const Container = forwardRef((props, ref) => <div ref={ref} {...props} className={classNames(containerCss, props.className)} />);
const dropdownMenuCss = "d1ik0hbw";
const DropdownMenu = forwardRef((props, ref) => <div ref={ref} {...props} className={classNames(dropdownMenuCss, props.className)} />);
const headerCss = "h1npfyf2";
const sectionCss = "sobmgb1";
const sectionWithBorderCss = "s1qqud6s";
const sectionLabelCss = "s9rs0es";
const optionCss = "of1034f";
const footerCss = "fuony0b";
const footerLinkCss = "f1bwexi9";
const footerArrowCss = "f7ioiz4";
const settingsButtonCss = "sjkv57n";
const settingsButtonLabelCss = "skbfldl";
const SettingsSection = ({
  label,
  withBorder = true,
  children
}) => <div className={classNames(sectionCss, {
  [sectionWithBorderCss]: withBorder
})}>
    <div className={sectionLabelCss}>
      <BodyText weight="bold">{label}</BodyText>
    </div>
    {children}
  </div>;
const SchedulingMethodSection = ({
  schedulingMethods,
  onChange
}) => {
  const {
    data: calendarData
  } = useFetchCalendarAccountsQuery();
  let pushCalendarName = null;
  if (calendarData?.ids?.length && calendarData.entities) {
    const pushCal = calendarData.ids.find(id => calendarData.entities[id]?.pushCalendarId);
    pushCalendarName = pushCal ? calendarData.entities[pushCal]?.calendarName ?? null : null;
  }
  const isCalendlyOnly = schedulingMethods.length === 1 && schedulingMethods.includes(SchedulingMethods.CALENDLY);
  const isAll = !isCalendlyOnly;
  const onSelect = value => {
    const newMethods = value === SchedulingMethods.ALL ? AllSchedulingMethods : [value];
    onChange(newMethods);
  };
  const connectedCalLabel = pushCalendarName ? I18n.t('publisher.meetings.persistent_settings.show_meetings.all_with_name', {
    name: pushCalendarName
  }) : I18n.t('publisher.meetings.persistent_settings.show_meetings.all');
  return <SettingsSection label={I18n.t('publisher.meetings.persistent_settings.show_meetings.label')} withBorder={false}>
      <label className={optionCss}>
        <RadioInput checked={isAll} onChange={() => onSelect(SchedulingMethods.ALL)} name="persistent-scheduling-method" data-testid="scheduling-method-all" />
        <BodyText>{connectedCalLabel}</BodyText>
      </label>
      <label className={optionCss}>
        <RadioInput checked={isCalendlyOnly} onChange={() => onSelect(SchedulingMethods.CALENDLY)} name="persistent-scheduling-method" data-testid="scheduling-method-calendly" />
        <BodyText>{I18n.t('publisher.meetings.persistent_settings.show_meetings.calendly_only')}</BodyText>
      </label>
    </SettingsSection>;
};
const buffersLabelCss = "bjq5m83";
const BuffersSection = ({
  showBuffers,
  onToggle
}) => <div className={classNames(sectionCss)}>
    <div className={buffersLabelCss}>
      <BodyText weight="bold">{I18n.t('publisher.meetings.filter_modal.buffers.label')}</BodyText>
      <ProTip>
        {I18n.t('publisher.scheduled_events.buffers.pro_tip_text')}
        {' '}
        <Link rel="noopener noreferrer" target="_blank" underline={false} href="https://help.calendly.com/hc/en-us/articles/14048208107287-How-to-use-buffers">
          <BodyText inline weight="bold" color={textColorSecondary4}>{I18n.t('publisher.action.learn_more')}</BodyText>
        </Link>
      </ProTip>
    </div>
    <label className={optionCss}>
      <RadioInput checked={showBuffers} onChange={() => onToggle(true)} name="persistent-buffers" data-testid="buffers-show" />
      <BodyText>{I18n.t('publisher.meetings.persistent_settings.buffers.show')}</BodyText>
    </label>
    <label className={optionCss}>
      <RadioInput checked={!showBuffers} onChange={() => onToggle(false)} name="persistent-buffers" data-testid="buffers-hide" />
      <BodyText>{I18n.t('publisher.meetings.persistent_settings.buffers.hide')}</BodyText>
    </label>
  </div>;
const NotetakerSettingsFooter = () => <div className={footerCss}>
    <Link href="/app/personal/ai_notetaker" underline={false} data-testid="notetaker-settings-link" className={footerLinkCss}>
      <AiNotetakerIcon color={colorTextStandard} />
      <BodyText color={colorTextStandard}>
        {I18n.t('publisher.meetings.notetaker_settings')}
      </BodyText>
      <span className={footerArrowCss}>
        <ArrowExpandIcon size="standard" color={colorTextStandard} />
      </span>
    </Link>
  </div>;
export const PersistentFiltersMenu = forwardRef(({
  notetakerEnabled = false
}, ref) => {
  const [toggleDropdown, Dropdown, isDropdownOpen] = useDropdown();
  const {
    data: userSettings
  } = useGetUserSettingsQuery();
  const isPhone = useDeviceType('isPhone');
  const [updateUserSettings] = usePatchUserSettingsMutation();
  const meetingsPageSettings = userSettings?.meetingsPageSettings || {};
  const showBuffers = meetingsPageSettings.showBuffers ?? true;
  const schedulingMethods = meetingsPageSettings.schedulingMethods?.length > 0 ? meetingsPageSettings.schedulingMethods : AllSchedulingMethods;
  const handleBuffersToggle = newShowBuffers => {
    updateUserSettings({
      user_settings: {
        meetingsPageSettings: {
          showBuffers: newShowBuffers,
          schedulingMethods
        }
      }
    });
  };
  const handleSchedulingMethodChange = newMethods => {
    updateUserSettings({
      user_settings: {
        meetingsPageSettings: {
          schedulingMethods: newMethods,
          showBuffers
        }
      }
    });
  };
  return <Container ref={ref}>
      <BareButton onClick={toggleDropdown} aria-expanded={isDropdownOpen} aria-label={I18n.t('publisher.meetings.settings')} className={settingsButtonCss} data-testid="settings-dropdown-button">
        <SettingsIcon />
        {!isPhone && <span className={settingsButtonLabelCss}>
            <BodyText>{I18n.t('publisher.meetings.settings')}</BodyText>
          </span>}
      </BareButton>
      <Dropdown>
        <DropdownMenu data-testid="persistent-filters-menu" onClick={e => e.stopPropagation()}>
          <div className={headerCss}>
            <BodyText weight="bold">
              {I18n.t('publisher.meetings.persistent_settings.title')}
            </BodyText>
          </div>
          <SchedulingMethodSection schedulingMethods={schedulingMethods} onChange={handleSchedulingMethodChange} />
          <BuffersSection showBuffers={showBuffers} onToggle={handleBuffersToggle} />
          {notetakerEnabled && <NotetakerSettingsFooter />}
        </DropdownMenu>
      </Dropdown>
    </Container>;
});

require("./persistent_filters_menu.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./persistent_filters_menu.js");